/**
 * O Financeiro além da baixa, sobre o PostgREST — trilha T5a.
 *
 * Contas e formas de pagamento com as INATIVAS (o `financeiro()` só traz as
 * ativas, e desativada que some da tela é desativada que ninguém reativa),
 * título avulso, transferência entre contas e conciliação por OFX (`0082`).
 */

import { supabase } from '@/supabase';
import { atual } from '@/session/sessao';
import { competenciaDoDia, primeiroDiaDoMesSeguinte } from './financeiroAvulso';
import type {
    CadastrosFinanceiros, DadosDaConta, DadosDoMetodo, DetalheDoTitulo, EdicaoDeTitulo, LinhaDoOfx, LinhaDoExtratoImportada,
    NovaTransferencia, NovoTitulo, PagamentoEstornado, SituacaoDaLinha, TipoDeMetodo, Transferencia,
} from './financeiroAvulso';
import type { PortaDeDados } from './porta';
import type { ContaFinanceira, Movimentacao, Titulo } from './tipos';

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

function clinicaId(): string {
    const id = atual()?.clinicaId;
    if (!id) throw new Error('Sessão sem clínica: entre novamente.');
    return id;
}

function erro(mensagem: string, e: { message: string; code?: string } | null): void {
    if (!e) return;
    if (e.code === '42501') throw new Error('Esta ação exige a permissão do financeiro.');
    throw new Error(`${mensagem}: ${e.message}`);
}

function linhas(data: unknown): Record<string, unknown>[] {
    return (data ?? []) as Record<string, unknown>[];
}

function nome(v: unknown): string | undefined {
    const x = Array.isArray(v) ? v[0] : v;
    return x && typeof x === 'object' ? ((x as { nome?: string }).nome ?? undefined) : undefined;
}

function textoOuNulo(v: string | undefined): string | null {
    const t = v?.trim();
    return t ? t : null;
}

function validarTitulo(dados: EdicaoDeTitulo): void {
    if (!dados.descricao.trim()) throw new Error('Descreva o lançamento.');
    if (!Number.isInteger(dados.valorCentavos) || dados.valorCentavos <= 0) throw new Error('Informe um valor maior que zero.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dados.vencimento)) throw new Error('Informe o vencimento.');
}

export type MetodosDoFinanceiroAvulso = Pick<PortaDeDados,
    | 'cadastrosFinanceiros' | 'salvarConta' | 'salvarMetodo'
    | 'criarTitulo' | 'detalheDoTitulo' | 'editarTitulo' | 'cancelarTitulo' | 'estornarPagamento'
    | 'transferencias' | 'transferirEntreContas'
    | 'extratoImportado' | 'importarExtrato' | 'conciliarLinhaDoExtrato' | 'ignorarLinhaDoExtrato'
>;

export const financeiroAvulsoNoSupabase: MetodosDoFinanceiroAvulso = {
    async cadastrosFinanceiros(): Promise<CadastrosFinanceiros> {
        const [contasResp, metodosResp] = await Promise.all([
            cliente().from('contas_financeiras').select('id, nome, tipo, banco, agencia, numero, saldo_inicial_centavos, ativa').order('nome'),
            cliente().from('metodos_pagamento').select('id, nome, tipo, taxa_percentual, prazo_dias, conta_id, ativo').order('nome'),
        ]);
        erro('Falha ao ler as contas', contasResp.error);
        erro('Falha ao ler as formas de pagamento', metodosResp.error);
        return {
            contas: linhas(contasResp.data).map((c) => ({
                id: String(c.id),
                nome: String(c.nome ?? ''),
                tipo: c.tipo as ContaFinanceira['tipo'],
                ...(c.banco ? { banco: String(c.banco) } : {}),
                ...(c.agencia ? { agencia: String(c.agencia) } : {}),
                ...(c.numero ? { numero: String(c.numero) } : {}),
                saldoInicialCentavos: Number(c.saldo_inicial_centavos ?? 0),
                ativa: Boolean(c.ativa),
            })),
            metodos: linhas(metodosResp.data).map((m) => ({
                id: String(m.id),
                nome: String(m.nome ?? ''),
                tipo: m.tipo as TipoDeMetodo,
                taxaPercentual: Number(m.taxa_percentual ?? 0),
                prazoDias: Number(m.prazo_dias ?? 0),
                ...(m.conta_id ? { contaId: String(m.conta_id) } : {}),
                ativo: Boolean(m.ativo),
            })),
        };
    },

    async salvarConta(id: string | null, dados: DadosDaConta): Promise<{ id: string }> {
        const nomeDaConta = dados.nome.trim();
        if (!nomeDaConta) throw new Error('A conta precisa de um nome.');
        if (!Number.isInteger(dados.saldoInicialCentavos)) throw new Error('Saldo inicial inválido.');
        const linha = {
            nome: nomeDaConta, tipo: dados.tipo, banco: textoOuNulo(dados.banco), agencia: textoOuNulo(dados.agencia),
            numero: textoOuNulo(dados.numero), saldo_inicial_centavos: dados.saldoInicialCentavos, ativa: dados.ativa,
        };
        const resposta = id
            ? await cliente().from('contas_financeiras').update(linha).eq('id', id).select('id').single()
            : await cliente().from('contas_financeiras').insert({ clinica_id: clinicaId(), ...linha }).select('id').single();
        if (resposta.error?.code === '23505') throw new Error(`Já existe uma conta chamada ${nomeDaConta}.`);
        erro('Falha ao salvar a conta', resposta.error);
        return { id: String((resposta.data as { id: string }).id) };
    },

    async salvarMetodo(id: string | null, dados: DadosDoMetodo): Promise<{ id: string }> {
        const nomeDoMetodo = dados.nome.trim();
        if (!nomeDoMetodo) throw new Error('A forma de pagamento precisa de um nome.');
        if (!(dados.taxaPercentual >= 0 && dados.taxaPercentual < 100)) throw new Error('A taxa vai de 0 a 99,99%.');
        if (!Number.isInteger(dados.prazoDias) || dados.prazoDias < 0) throw new Error('O prazo de repasse é um número de dias, zero ou mais.');
        const linha = {
            nome: nomeDoMetodo, tipo: dados.tipo, taxa_percentual: dados.taxaPercentual, prazo_dias: dados.prazoDias,
            conta_id: dados.contaId ?? null, ativo: dados.ativo,
        };
        const resposta = id
            ? await cliente().from('metodos_pagamento').update(linha).eq('id', id).select('id').single()
            : await cliente().from('metodos_pagamento').insert({ clinica_id: clinicaId(), ...linha }).select('id').single();
        if (resposta.error?.code === '23505') throw new Error(`Já existe uma forma de pagamento chamada ${nomeDoMetodo}.`);
        erro('Falha ao salvar a forma de pagamento', resposta.error);
        return { id: String((resposta.data as { id: string }).id) };
    },

    async criarTitulo(novo: NovoTitulo): Promise<{ id: string }> {
        validarTitulo(novo);
        if (novo.tipo === 'receber' && !novo.contatoId) throw new Error('Escolha o paciente do título a receber.');
        if (novo.tipo === 'pagar' && !novo.fornecedor?.trim()) throw new Error('Informe o fornecedor da conta a pagar.');
        const { data, error } = await cliente().from('titulos').insert({
            clinica_id: clinicaId(),
            tipo: novo.tipo,
            descricao: novo.descricao.trim(),
            contato_id: novo.tipo === 'receber' ? novo.contatoId ?? null : null,
            fornecedor: novo.tipo === 'pagar' ? textoOuNulo(novo.fornecedor) : null,
            categoria_id: novo.categoriaId ?? null,
            valor_centavos: novo.valorCentavos,
            vencimento: novo.vencimento,
            /* A coluna é `date`: a competência é o primeiro dia do mês. */
            competencia: `${novo.competencia ?? competenciaDoDia(novo.vencimento)}-01`,
            parcela: 1,
            parcelas_total: 1,
            situacao: 'aberto',
            observacoes: textoOuNulo(novo.observacoes),
        }).select('id').single();
        erro('Falha ao criar o lançamento', error);
        return { id: String((data as { id: string }).id) };
    },

    async detalheDoTitulo(id: string): Promise<DetalheDoTitulo> {
        const [{ data, error }, estornosResp] = await Promise.all([
            cliente().from('titulos')
                .select('id, tipo, descricao, contato_id, fornecedor, categoria_id, valor_centavos, vencimento, competencia, parcela, parcelas_total, situacao, observacoes, contatos(nome), categorias_financeiras(nome), pagamentos(id, valor_centavos, taxa_centavos, pago_em, liquidado_em, contas_financeiras(nome), metodos_pagamento(nome))')
                .eq('id', id).single(),
            cliente().from('pagamentos_estornados')
                .select('id, valor_centavos, pago_em, motivo, estornado_em')
                .eq('titulo_id', id).order('estornado_em', { ascending: false }),
        ]);
        erro('Falha ao ler o título', error);
        erro('Falha ao ler os estornos do título', estornosResp.error);
        const estornos = linhas(estornosResp.data).map((e): PagamentoEstornado => ({
            id: String(e.id),
            valorCentavos: Number(e.valor_centavos ?? 0),
            pagoEm: String(e.pago_em ?? ''),
            ...(e.motivo ? { motivo: String(e.motivo) } : {}),
            estornadoEm: String(e.estornado_em ?? ''),
        }));
        const t = data as unknown as Record<string, unknown>;
        const pagamentos = linhas(t.pagamentos);
        const titulo: Titulo = {
            id: String(t.id),
            tipo: t.tipo as Titulo['tipo'],
            descricao: String(t.descricao ?? ''),
            contraparte: nome(t.contatos) ?? (t.fornecedor as string | null) ?? '—',
            ...(t.contato_id ? { contatoId: String(t.contato_id) } : {}),
            ...(nome(t.categorias_financeiras) ? { categoria: nome(t.categorias_financeiras) } : {}),
            ...(t.categoria_id ? { categoriaId: String(t.categoria_id) } : {}),
            valorCentavos: Number(t.valor_centavos ?? 0),
            pagoCentavos: pagamentos.reduce((s, p) => s + Number(p.valor_centavos ?? 0), 0),
            vencimento: String(t.vencimento ?? ''),
            competencia: String(t.competencia ?? ''),
            parcela: Number(t.parcela ?? 1),
            parcelasTotal: Number(t.parcelas_total ?? 1),
            situacao: t.situacao as Titulo['situacao'],
        };
        return {
            titulo,
            ...(t.categoria_id ? { categoriaId: String(t.categoria_id) } : {}),
            ...(t.fornecedor ? { fornecedor: String(t.fornecedor) } : {}),
            ...(t.observacoes ? { observacoes: String(t.observacoes) } : {}),
            pagamentos: pagamentos.map((p): Movimentacao => {
                const valor = Number(p.valor_centavos ?? 0);
                const taxa = Number(p.taxa_centavos ?? 0);
                return {
                    id: String(p.id),
                    tituloId: titulo.id,
                    tipo: titulo.tipo,
                    descricao: titulo.descricao,
                    contraparte: titulo.contraparte,
                    valorCentavos: valor,
                    taxaCentavos: taxa,
                    liquidoCentavos: valor - taxa,
                    pagoEm: String(p.pago_em ?? ''),
                    ...(p.liquidado_em ? { liquidadoEm: String(p.liquidado_em) } : {}),
                    ...(nome(p.contas_financeiras) ? { conta: nome(p.contas_financeiras) } : {}),
                    ...(nome(p.metodos_pagamento) ? { metodo: nome(p.metodos_pagamento) } : {}),
                };
            }).sort((a, b) => b.pagoEm.localeCompare(a.pagoEm)),
            estornos,
        };
    },

    async editarTitulo(id: string, edicao: EdicaoDeTitulo): Promise<void> {
        validarTitulo(edicao);
        const detalhe = await this.detalheDoTitulo(id);
        if (detalhe.titulo.situacao === 'cancelado') throw new Error('Título cancelado não se edita.');
        /* Baixar o valor para menos do que já foi pago faria o título "quitado
           com sobra" — dinheiro que entrou sem nada que o explique. */
        if (edicao.valorCentavos < detalhe.titulo.pagoCentavos) {
            throw new Error(`O valor não pode ficar abaixo do que já foi pago (${(detalhe.titulo.pagoCentavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}).`);
        }
        const tipo = detalhe.titulo.tipo;
        const { error } = await cliente().from('titulos').update({
            descricao: edicao.descricao.trim(),
            contato_id: tipo === 'receber' ? edicao.contatoId ?? null : null,
            fornecedor: tipo === 'pagar' ? textoOuNulo(edicao.fornecedor) : null,
            categoria_id: edicao.categoriaId ?? null,
            valor_centavos: edicao.valorCentavos,
            vencimento: edicao.vencimento,
            competencia: `${edicao.competencia ?? competenciaDoDia(edicao.vencimento)}-01`,
            observacoes: textoOuNulo(edicao.observacoes),
            /* Sem `situacao`: o gatilho `trava_do_titulo` (0175) a recalcula
               quando o valor muda e recusa valor abaixo do já pago — inclusive
               quando outra pessoa deu baixa entre a leitura acima e esta escrita. */
        }).eq('id', id);
        erro('Falha ao salvar o título', error);
    },

    async cancelarTitulo(id: string): Promise<void> {
        const detalhe = await this.detalheDoTitulo(id);
        if (detalhe.pagamentos.length > 0) {
            throw new Error('Este título já tem pagamento registrado. Cancelar apagaria do histórico um dinheiro que andou.');
        }
        const { error } = await cliente().from('titulos').update({ situacao: 'cancelado' }).eq('id', id);
        erro('Falha ao cancelar o título', error);
    },

    async estornarPagamento(pagamentoId: string, motivo: string): Promise<void> {
        if (!motivo.trim()) throw new Error('Diga o motivo do estorno.');
        const { error } = await cliente().rpc('estornar_pagamento', { p_pagamento: pagamentoId, p_motivo: motivo.trim() });
        erro('Falha ao estornar o pagamento', error);
    },

    async transferencias(mes?: string): Promise<Transferencia[]> {
        let consulta = cliente().from('transferencias_entre_contas')
            .select('id, conta_origem_id, conta_destino_id, valor_centavos, data, observacao').order('data', { ascending: false });
        if (mes) consulta = consulta.lt('data', primeiroDiaDoMesSeguinte(mes));
        const [transferenciasResp, contasResp] = await Promise.all([
            consulta,
            cliente().from('contas_financeiras').select('id, nome'),
        ]);
        erro('Falha ao ler as transferências', transferenciasResp.error);
        erro('Falha ao ler as contas', contasResp.error);
        const nomes = new Map(linhas(contasResp.data).map((c) => [String(c.id), String(c.nome ?? '')]));
        return linhas(transferenciasResp.data).map((t) => ({
            id: String(t.id),
            origemId: String(t.conta_origem_id),
            origem: nomes.get(String(t.conta_origem_id)) ?? '—',
            destinoId: String(t.conta_destino_id),
            destino: nomes.get(String(t.conta_destino_id)) ?? '—',
            valorCentavos: Number(t.valor_centavos ?? 0),
            data: String(t.data ?? ''),
            ...(t.observacao ? { observacao: String(t.observacao) } : {}),
        }));
    },

    async transferirEntreContas(nova: NovaTransferencia): Promise<{ id: string }> {
        if (nova.origemId === nova.destinoId) throw new Error('Escolha duas contas diferentes.');
        if (!Number.isInteger(nova.valorCentavos) || nova.valorCentavos <= 0) throw new Error('Informe um valor maior que zero.');
        if (!/^\d{4}-\d{2}-\d{2}$/.test(nova.data)) throw new Error('Informe a data da transferência.');
        const { data, error } = await cliente().from('transferencias_entre_contas').insert({
            clinica_id: clinicaId(), conta_origem_id: nova.origemId, conta_destino_id: nova.destinoId,
            valor_centavos: nova.valorCentavos, data: nova.data, observacao: textoOuNulo(nova.observacao),
        }).select('id').single();
        if (error?.code === '23503') throw new Error('Uma das contas não é desta clínica.');
        erro('Falha ao registrar a transferência', error);
        return { id: String((data as { id: string }).id) };
    },

    async extratoImportado(contaId: string): Promise<LinhaDoExtratoImportada[]> {
        const { data, error } = await cliente().from('extrato_importado')
            .select('id, conta_id, fitid, data, valor_centavos, descricao, situacao, titulo_id')
            .eq('conta_id', contaId).order('data', { ascending: false });
        erro('Falha ao ler o extrato importado', error);
        const lidas = linhas(data);
        const idsDeTitulo = [...new Set(lidas.map((l) => l.titulo_id).filter((x): x is string => typeof x === 'string'))];
        const descricoes = new Map<string, string>();
        if (idsDeTitulo.length) {
            const titulosResp = await cliente().from('titulos').select('id, descricao').in('id', idsDeTitulo);
            erro('Falha ao ler os títulos conciliados', titulosResp.error);
            for (const t of linhas(titulosResp.data)) descricoes.set(String(t.id), String(t.descricao ?? ''));
        }
        return lidas.map((l) => ({
            id: String(l.id),
            contaId: String(l.conta_id),
            fitid: String(l.fitid ?? ''),
            data: String(l.data ?? ''),
            valorCentavos: Number(l.valor_centavos ?? 0),
            descricao: String(l.descricao ?? ''),
            situacao: l.situacao as SituacaoDaLinha,
            ...(l.titulo_id ? { tituloId: String(l.titulo_id), tituloDescricao: descricoes.get(String(l.titulo_id)) ?? '' } : {}),
        }));
    },

    async importarExtrato(contaId: string, lidas: LinhaDoOfx[]): Promise<{ novas: number; repetidas: number }> {
        if (!lidas.length) return { novas: 0, repetidas: 0 };
        const clinica = clinicaId();
        /* `ignoreDuplicates`: a linha que já existe (mesmo FITID na mesma conta)
           fica como está — inclusive a que já foi conciliada. */
        const { data, error } = await cliente().from('extrato_importado').upsert(
            lidas.map((l) => ({
                clinica_id: clinica, conta_id: contaId, fitid: l.fitid, data: l.data,
                valor_centavos: l.valorCentavos, descricao: l.descricao,
            })),
            { onConflict: 'clinica_id,conta_id,fitid', ignoreDuplicates: true },
        ).select('id');
        erro('Falha ao importar o extrato', error);
        const novas = linhas(data).length;
        return { novas, repetidas: lidas.length - novas };
    },

    async conciliarLinhaDoExtrato(linhaId: string, tituloId: string): Promise<void> {
        const { error } = await cliente().rpc('conciliar_linha_do_extrato', { p_linha: linhaId, p_titulo: tituloId });
        erro('Falha ao conciliar', error);
    },

    async ignorarLinhaDoExtrato(linhaId: string): Promise<void> {
        const { error } = await cliente().from('extrato_importado')
            .update({ situacao: 'ignorada' }).eq('id', linhaId).eq('situacao', 'pendente');
        erro('Falha ao ignorar a linha', error);
    },
};
